import { client } from "./supabase";

// ─── Helpers ─────────────────────────────────────────────

const montoAprox = (a, b, tolerancia = 0.03) => {
  if (!a || !b) return false;
  const diff = Math.abs(Number(a) - Number(b));
  return diff / Math.max(Number(a), Number(b)) <= tolerancia;
};

const textoContiene = (texto, patron) => {
  if (!texto || !patron) return false;
  return texto.toUpperCase().includes(patron.toUpperCase());
};

// ─── Carga de datos de referencia ────────────────────────

const cargarDatosReferencia = async (pais) => {
  const [reglasRes, proveedoresRes, clientesRes] = await Promise.all([
    client
      .from("reglas_matching")
      .select("*")
      .eq("pais", pais)
      .eq("activa", true)
      .order("prioridad", { ascending: false })
      .order("veces_confirmada", { ascending: false }),
    client
      .from("proveedores")
      .select("id, nombre, rut_ruc, alias_banco, cuenta_er_default")
      .eq("pais", pais)
      .eq("habilitado", true),
    client.from("clientes").select("id, nombre, empresa, rut, razon_social").eq("pais", pais),
  ]);

  if (reglasRes.error) throw reglasRes.error;
  if (proveedoresRes.error) throw proveedoresRes.error;
  if (clientesRes.error) throw clientesRes.error;

  return {
    reglas: reglasRes.data || [],
    proveedores: proveedoresRes.data || [],
    clientes: clientesRes.data || [],
  };
};

// ─── Match EGRESOS ───────────────────────────────────────

const matchEgreso = (mov, { reglas, proveedores, facturasRecibidas, facturasProveedor }) => {
  const desc = mov.descripcion_raw || "";
  const monto = Number(mov.monto);

  // PASO 1: Factura SII recibida con monto exacto (0.99)
  for (const f of facturasRecibidas) {
    if (montoAprox(monto, f.monto_total, 0.005)) {
      const prov = proveedores.find((p) => p.rut_ruc === f.rut_emisor);
      return {
        match_entidad_tipo: "proveedor",
        match_entidad_id: prov?.id || null,
        match_entidad_nombre: f.razon_social_emisor,
        match_factura_tipo: "factura_sii_recibida",
        match_factura_id: f.id,
        match_confianza: 0.99,
        match_razon: "sii_monto_exacto",
      };
    }
  }

  // PASO 2: Reglas explícitas aprendidas (0.95)
  for (const regla of reglas) {
    if (textoContiene(desc, regla.patron)) {
      return {
        match_entidad_tipo: regla.match_entidad_tipo,
        match_entidad_id: regla.match_entidad_id,
        match_entidad_nombre: regla.match_entidad_nombre,
        match_factura_tipo: null,
        match_factura_id: null,
        match_confianza: 0.95,
        match_razon: "regla",
      };
    }
  }

  // PASO 3: Alias banco proveedor + factura pendiente ±3% (0.90)
  for (const prov of proveedores) {
    if (!prov.alias_banco?.length) continue;
    const aliasMatch = prov.alias_banco.some((alias) => textoContiene(desc, alias));
    if (!aliasMatch) continue;

    const facturaMatch = facturasProveedor.find((f) => f.proveedor_id === prov.id && montoAprox(monto, f.monto_total));
    if (facturaMatch) {
      return {
        match_entidad_tipo: "proveedor",
        match_entidad_id: prov.id,
        match_entidad_nombre: prov.nombre,
        match_factura_tipo: "factura_proveedor",
        match_factura_id: facturaMatch.id,
        match_confianza: 0.9,
        match_razon: "alias_banco",
      };
    }

    // PASO 4: Alias banco sin factura (0.80)
    return {
      match_entidad_tipo: "proveedor",
      match_entidad_id: prov.id,
      match_entidad_nombre: prov.nombre,
      match_factura_tipo: null,
      match_factura_id: null,
      match_confianza: 0.8,
      match_razon: "alias_banco",
    };
  }

  // PASO 5: Monto exacto contra facturas_proveedor pendientes (0.75)
  const facturaMontoExacto = facturasProveedor.find((f) => montoAprox(monto, f.monto_total));
  if (facturaMontoExacto) {
    const prov = proveedores.find((p) => p.id === facturaMontoExacto.proveedor_id);
    return {
      match_entidad_tipo: "proveedor",
      match_entidad_id: facturaMontoExacto.proveedor_id,
      match_entidad_nombre: prov?.nombre || null,
      match_factura_tipo: "factura_proveedor",
      match_factura_id: facturaMontoExacto.id,
      match_confianza: 0.75,
      match_razon: "monto_exacto",
    };
  }

  return null;
};

// ─── Match INGRESOS ──────────────────────────────────────

const matchIngreso = (mov, { clientes, facturasEmitidas }) => {
  const desc = mov.descripcion_raw || "";
  const monto = Number(mov.monto);

  // PASO 1: Folio en descripcion_raw (0.99)
  for (const f of facturasEmitidas) {
    if (textoContiene(desc, String(f.folio))) {
      const cli = clientes.find((c) => c.rut === f.rut_receptor);
      return {
        match_entidad_tipo: "cliente",
        match_entidad_id: cli?.id || null,
        match_entidad_nombre: f.razon_social_receptor,
        match_factura_tipo: "factura_sii_emitida",
        match_factura_id: f.id,
        match_confianza: 0.99,
        match_razon: "folio_en_descripcion",
      };
    }
  }

  // PASO 2: Monto exacto de factura emitida (0.95)
  for (const f of facturasEmitidas) {
    if (montoAprox(monto, f.monto_total, 0.005)) {
      const cli = clientes.find((c) => c.rut === f.rut_receptor);
      return {
        match_entidad_tipo: "cliente",
        match_entidad_id: cli?.id || null,
        match_entidad_nombre: f.razon_social_receptor,
        match_factura_tipo: "factura_sii_emitida",
        match_factura_id: f.id,
        match_confianza: 0.95,
        match_razon: "sii_monto_exacto",
      };
    }
  }

  // PASO 3: Monto ±3% contra facturas pendientes de cobro (0.85)
  for (const f of facturasEmitidas) {
    if (montoAprox(monto, f.monto_total)) {
      const cli = clientes.find((c) => c.rut === f.rut_receptor);
      return {
        match_entidad_tipo: "cliente",
        match_entidad_id: cli?.id || null,
        match_entidad_nombre: f.razon_social_receptor,
        match_factura_tipo: "factura_sii_emitida",
        match_factura_id: f.id,
        match_confianza: 0.85,
        match_razon: "monto_aproximado",
      };
    }
  }

  // PASO 4: RUT o razón social del cliente en descripcion (0.75)
  for (const cli of clientes) {
    const matchRut = cli.rut && textoContiene(desc, cli.rut);
    const matchRazon = cli.razon_social && textoContiene(desc, cli.razon_social);
    const matchEmpresa = cli.empresa && textoContiene(desc, cli.empresa);
    if (matchRut || matchRazon || matchEmpresa) {
      return {
        match_entidad_tipo: "cliente",
        match_entidad_id: cli.id,
        match_entidad_nombre: cli.razon_social || cli.empresa || cli.nombre,
        match_factura_tipo: null,
        match_factura_id: null,
        match_confianza: 0.75,
        match_razon: matchRut ? "rut_en_descripcion" : "nombre_en_descripcion",
      };
    }
  }

  return null;
};

// ─── Ejecutar matching sobre movimientos pendientes ──────

export const ejecutarMatching = async (pais, movimientosPendientes) => {
  const ref = await cargarDatosReferencia(pais);

  // Cargar facturas SII del período relevante
  const fechas = movimientosPendientes.map((m) => m.fecha).filter(Boolean);
  const fechaMin = fechas.length > 0 ? fechas.reduce((a, b) => (a < b ? a : b)) : null;
  const fechaMax = fechas.length > 0 ? fechas.reduce((a, b) => (a > b ? a : b)) : null;

  const queryEmitidas = async () => {
    const { data: todas } = await client
      .from("facturas_sii_emitidas")
      .select("*")
      .eq("pais", pais)
      .is("facturafechacobro", null)
      .gte("fecha_emision", fechaMin)
      .lte("fecha_emision", fechaMax);
    const { data: yaVinculadas } = await client
      .from("movimientos_bancarios")
      .select("factura_sii_emitida_id")
      .eq("pais", pais)
      .not("factura_sii_emitida_id", "is", null);
    const idsVinculados = new Set((yaVinculadas || []).map((m) => m.factura_sii_emitida_id));
    return (todas || []).filter((f) => !idsVinculados.has(f.id));
  };

  const queryRecibidas = async () => {
    const { data: todas } = await client
      .from("facturas_sii_recibidas")
      .select("*")
      .eq("pais", pais)
      .gte("fecha_emision", fechaMin)
      .lte("fecha_emision", fechaMax);
    const { data: yaVinculadas } = await client
      .from("movimientos_bancarios")
      .select("factura_sii_recibida_id")
      .eq("pais", pais)
      .not("factura_sii_recibida_id", "is", null);
    const idsVinculados = new Set((yaVinculadas || []).map((m) => m.factura_sii_recibida_id));
    return (todas || []).filter((f) => !idsVinculados.has(f.id));
  };

  const queryFP = () =>
    client
      .from("facturas_proveedor")
      .select("id, proveedor_id, monto_total, estado")
      .eq("pais", pais)
      .eq("estado", "pendiente");

  const [emitidas, recibidas, fpRes] = await Promise.all([
    fechaMin ? queryEmitidas() : [],
    fechaMin ? queryRecibidas() : [],
    queryFP(),
  ]);

  if (fpRes.error) throw fpRes.error;

  const contexto = {
    reglas: ref.reglas,
    proveedores: ref.proveedores,
    clientes: ref.clientes,
    facturasEmitidas: emitidas,
    facturasRecibidas: recibidas,
    facturasProveedor: fpRes.data || [],
  };

  const resultados = [];

  for (const mov of movimientosPendientes) {
    const match = mov.tipo === "egreso" ? matchEgreso(mov, contexto) : matchIngreso(mov, contexto);

    if (match) {
      resultados.push({ movimiento_id: mov.id, ...match });
    }
  }

  return resultados;
};

// ─── Aplicar matches a la DB ─────────────────────────────

export const aplicarMatches = async (matches) => {
  let aplicados = 0;
  for (const match of matches) {
    const { movimiento_id, ...matchData } = match;
    const { error } = await client
      .from("movimientos_bancarios")
      .update(matchData)
      .eq("id", movimiento_id)
      .eq("estado", "pendiente");
    if (!error) aplicados++;
  }
  return { aplicados, total: matches.length };
};

// ─── Feedback loop: crear/actualizar regla ───────────────

export const registrarRegla = async (
  pais,
  { patron, match_entidad_tipo, match_entidad_id, match_entidad_nombre, cuenta_er, categoria_pago }
) => {
  // Buscar regla existente con el mismo patrón
  const { data: existente } = await client
    .from("reglas_matching")
    .select("id, veces_confirmada")
    .eq("pais", pais)
    .eq("patron", patron)
    .limit(1)
    .single();

  if (existente) {
    const { data, error } = await client
      .from("reglas_matching")
      .update({
        match_entidad_tipo,
        match_entidad_id,
        match_entidad_nombre,
        cuenta_er,
        categoria_pago,
        veces_confirmada: (existente.veces_confirmada || 0) + 1,
        updated_at: new Date().toISOString(),
      })
      .eq("id", existente.id)
      .select();
    if (error) throw error;
    return data?.[0];
  }

  const { data, error } = await client
    .from("reglas_matching")
    .insert([
      {
        pais,
        patron,
        match_entidad_tipo,
        match_entidad_id,
        match_entidad_nombre,
        cuenta_er,
        categoria_pago,
        creada_por: "usuario",
      },
    ])
    .select();
  if (error) throw error;
  return data?.[0];
};

export const incrementarConfirmacion = async (reglaId) => {
  const { data: regla } = await client.from("reglas_matching").select("veces_confirmada").eq("id", reglaId).single();

  if (!regla) return;

  const { error } = await client
    .from("reglas_matching")
    .update({
      veces_confirmada: (regla.veces_confirmada || 0) + 1,
      updated_at: new Date().toISOString(),
    })
    .eq("id", reglaId);
  if (error) throw error;
};

// ─── Reglas CRUD ─────────────────────────────────────────

export const getReglas = async (pais) => {
  const { data, error } = await client
    .from("reglas_matching")
    .select("*")
    .eq("pais", pais)
    .order("prioridad", { ascending: false })
    .order("veces_confirmada", { ascending: false });
  if (error) throw error;
  return data || [];
};

export const desactivarRegla = async (id) => {
  const { error } = await client
    .from("reglas_matching")
    .update({ activa: false, updated_at: new Date().toISOString() })
    .eq("id", id);
  if (error) throw error;
};
